import { memoize } from 'lodash'
import React from 'react'

import { useGetViewerQuery } from '@/generated/graphql'

const getImage = memoize(async (content: string) => {
  const canvas = document.createElement('canvas')
  canvas.setAttribute('width', '150x')
  canvas.setAttribute('height', '100px')
  const ctx = canvas.getContext('2d')!
  ctx.translate(0, 10)
  ctx.font = ctx.font.replace(/\d+px/, '18px')
  ctx.fillStyle = 'rgba(0, 0, 0, 0.03)'
  ctx.rotate((Math.PI / 180) * 45)
  ctx.fillText(content, 0, 0)
  const url = await new Promise((resolve) => canvas.toBlob((blob) => resolve(URL.createObjectURL(blob))))
  canvas.remove()
  return url
})

const getWatermarkDiv = async (content: string) => {
  const style = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundRepeat: 'repeat',
  }
  const base64Url = await getImage(content)
  const div = document.createElement('div')
  for (const key of Object.keys(style)) {
    div.style.setProperty(key, style[key], 'important')
  }
  div.style.setProperty('pointer-events', 'none', 'important')
  div.style.setProperty('background-image', `url('${base64Url}')`)
  return div
}

export const Watermark = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({ children }, outerRef: any) => {
    const { data } = useGetViewerQuery()
    const username = data!.viewer.email.split('@')[0]
    const ref = outerRef || React.useRef<HTMLDivElement>(null)
    const [watermark, setDiv] = React.useState<HTMLDivElement | null>(null)

    React.useEffect(() => {
      getWatermarkDiv(username).then((div) => setDiv(div))
    }, [])

    React.useEffect(() => {
      const parentNode = ref.current
      if (!parentNode || !watermark) {
        return
      }

      parentNode.style.position = 'relative'

      const tryInsertNode = () => {
        if (parentNode.firstChild != watermark) {
          parentNode.insertBefore(watermark, parentNode.firstChild)
        }
      }

      tryInsertNode()

      const originalStyle = watermark.getAttribute('style')!

      if (!window.MutationObserver) {
        return
      }

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          switch (mutation.type) {
            case 'childList': {
              // watermark 节点被删除, 把它加回来
              if (Array.prototype.find.call(mutation.removedNodes, (n: Node) => n == watermark)) {
                tryInsertNode()
              }
              break
            }
            case 'attributes': {
              // 属性有变
              if (mutation.target == watermark && watermark.getAttribute('style') !== originalStyle) {
                watermark.setAttribute('style', originalStyle)
              }
              break
            }
          }
        })
      })

      observer.observe(parentNode, {
        attributes: true,
        childList: true,
        subtree: true,
      })

      return () => {
        if (window.MutationObserver) {
          observer.disconnect()
        }
        watermark.remove()
      }
    }, [ref.current, watermark])

    return React.cloneElement(children as any, {
      ref,
    })
  }
)
